import {
  Badge,
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaTrash } from 'react-icons/fa';
import { findRecurrencyLink, removePlanFromRecurrencyLink } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import { RecurrencyPeriodDescription } from '../components/recurrency-type';
import AddPlanToLink from './AddPlanToLink';
import EditLinkDetails from './EditLinkDetails';

interface ViewLinkRecurrenceProps {
  isOpen: boolean;
  onClose: () => void;
  linkId: string;
}

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper?: string;
}> = ({ label, value, helper }) => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4} bg="white">
    <Text fontSize="sm" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="semibold" color="gray.800">
      {value}
    </Text>
    {helper ? (
      <Text fontSize="sm" color="gray.500" mt={1}>
        {helper}
      </Text>
    ) : null}
  </Box>
);

const ViewLinkRecurrence: React.FC<ViewLinkRecurrenceProps> = ({ isOpen, onClose, linkId }) => {
  const { data: link } = useQuery({
    queryKey: ['findRecurrencyLink', linkId],
    queryFn: () => findRecurrencyLink(linkId),
    enabled: isOpen,
  });

  const summary = useMemo(() => {
    const itens = link?.itens ?? [];
    const totalAmount = itens.reduce((acc, item) => acc + Number(item.plan.amount ?? 0), 0);
    const activePlans = itens.filter((item) => item.plan.status).length;

    return {
      plans: itens.length,
      totalAmount,
      activePlans,
      averageAmount: itens.length ? totalAmount / itens.length : 0,
    };
  }, [link?.itens]);

  const handleRemoveItem = (itemId: string) => {
    removePlanFromRecurrencyLink(linkId, { planId: itemId })
      .then(() => {
        queryClient.invalidateQueries(['findRecurrencyLink', linkId]);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justify="space-between" align="center" gap={4}>
            <Box>
              <Text fontSize="2xl" fontWeight="bold">
                Detalhes do Link de Recorrência
              </Text>
              <Text color="gray.500" fontSize="sm">
                Visualize o link, seus metadados e os planos disponíveis para contratação.
              </Text>
            </Box>
            {link ? <EditLinkDetails linkId={linkId} /> : null}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {link && (
            <VStack align="stretch" spacing={6}>
              <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6}>
                <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" overflow="hidden" bg="gray.50">
                  {link.image ? (
                    <Image src={link.image} alt={link.title} w="100%" h="100%" minH="260px" objectFit="cover" />
                  ) : (
                    <Flex minH="260px" align="center" justify="center" direction="column" px={6}>
                      <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                        Sem imagem cadastrada
                      </Text>
                      <Text color="gray.500" mt={2} textAlign="center">
                        Adicione uma imagem para deixar o link de recorrência mais atrativo para o cliente final.
                      </Text>
                    </Flex>
                  )}
                </Box>

                <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" p={6} gridColumn={{ xl: 'span 2' }}>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontSize="3xl" fontWeight="bold" color="gray.800">
                        {link.title}
                      </Text>
                      <Text color="gray.500" mt={2}>
                        {link.description || 'Sem descrição cadastrada para este link de recorrência.'}
                      </Text>
                    </Box>

                    <HStack spacing={3} flexWrap="wrap">
                      <Badge colorScheme="blue">#{link.id}</Badge>
                      <Badge colorScheme={link.shareToPromoter ? 'green' : 'gray'}>
                        {link.shareToPromoter ? 'Compartilhado com promoters' : 'Uso interno'}
                      </Badge>
                      <Badge colorScheme="purple">{summary.plans} planos vinculados</Badge>
                    </HStack>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
                        <Text fontSize="sm" color="gray.500" mb={1}>
                          Cliente
                        </Text>
                        <Text fontWeight="semibold" color="gray.800">
                          {link.customer?.name ?? '---'}
                        </Text>
                      </Box>
                      <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
                        <Text fontSize="sm" color="gray.500" mb={1}>
                          Criado em
                        </Text>
                        <Text fontWeight="semibold" color="gray.800">
                          {DateTime.fromISO(link.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
                <SummaryCard label="Planos vinculados" value={summary.plans} helper={`${summary.activePlans} ativos`} />
                <SummaryCard label="Valor base somado" value={maskCurrency(summary.totalAmount)} />
                <SummaryCard label="Ticket médio" value={maskCurrency(summary.averageAmount)} />
                <SummaryCard
                  label="Compartilhamento"
                  value={link.shareToPromoter ? 'Ativo' : 'Desligado'}
                  helper="Controle de distribuição para promoters"
                />
              </SimpleGrid>

              <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" p={6}>
                <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={4} mb={4} flexWrap="wrap">
                  <Box>
                    <Text fontSize="xl" fontWeight="semibold" color="gray.800">
                      Planos vinculados
                    </Text>
                    <Text color="gray.500">
                      Gerencie os planos disponíveis neste link e remova combinações que não devem mais ser ofertadas.
                    </Text>
                  </Box>
                  <AddPlanToLink linkId={linkId} />
                </Flex>

                {link.itens.length ? (
                  <TableContainer border="1px solid" borderColor="gray.200" borderRadius="xl">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Plano</Th>
                          <Th isNumeric>Valor</Th>
                          <Th>Período</Th>
                          <Th>Status</Th>
                          <Th width="100px">Ações</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {link.itens.map((item) => (
                          <Tr key={item.id}>
                            <Td fontWeight="medium">{item.plan.description}</Td>
                            <Td isNumeric>{maskCurrency(Number(item.plan.amount))}</Td>
                            <Td>
                              <Badge colorScheme="purple">{RecurrencyPeriodDescription[item.plan.period]}</Badge>
                            </Td>
                            <Td>
                              <Badge colorScheme={item.plan.status ? 'green' : 'red'}>
                                {item.plan.status ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <Tooltip label="Remover">
                                  <IconButton
                                    icon={<FaTrash />}
                                    aria-label="Remover item"
                                    size="sm"
                                    colorScheme="red"
                                    onClick={() => handleRemoveItem(item.id)}
                                  />
                                </Tooltip>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box border="1px dashed" borderColor="gray.300" borderRadius="xl" px={6} py={10} textAlign="center">
                    <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                      Nenhum plano vinculado
                    </Text>
                    <Text color="gray.500" mt={2}>
                      Use o botão acima para adicionar os primeiros planos e transformar este link em uma vitrine pronta para venda.
                    </Text>
                  </Box>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ViewLinkRecurrence;
